import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
  Box,
  Alert,
  InputAdornment,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'

interface Invoice {
  id: string
  invoice_number: string
  total_amount: number
  paid_amount: number
  outstanding_amount: number
  balance_amount?: number  // Alias for backward compatibility
  status: string
}

interface PaymentData {
  amount: number
  payment_method: string
  payment_date: Date
  transaction_id: string
  notes: string
}

interface PaymentRecordDialogProps {
  open: boolean
  invoice: Invoice
  onClose: () => void
  onSubmit: (paymentData: PaymentData) => Promise<void>
}

const PaymentRecordDialog: React.FC<PaymentRecordDialogProps> = ({
  open,
  invoice,
  onClose,
  onSubmit,
}) => {
  const balanceAmount = invoice.outstanding_amount || invoice.balance_amount || 0
  const [amount, setAmount] = useState<string>(balanceAmount.toString())
  const [paymentMethod, setPaymentMethod] = useState<string>('bank_transfer')
  const [paymentDate, setPaymentDate] = useState<Date>(new Date())
  const [transactionId, setTransactionId] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>('')

  const paymentMethods = [
    { value: 'cash', label: 'Cash' },
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'upi', label: 'UPI' },
    { value: 'credit_card', label: 'Credit Card' },
    { value: 'debit_card', label: 'Debit Card' },
  ]

  const handleSubmit = async () => {
    setError('')

    // Validation
    const amountNum = parseFloat(amount)
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid payment amount')
      return
    }

    if (amountNum > balanceAmount) {
      setError(`Payment amount cannot exceed balance amount of ₹${balanceAmount.toLocaleString('en-IN')}`)
      return
    }

    if (!paymentMethod) {
      setError('Please select a payment method')
      return
    }

    setLoading(true)
    try {
      await onSubmit({
        amount: amountNum,
        payment_method: paymentMethod,
        payment_date: paymentDate,
        transaction_id: transactionId,
        notes: notes,
      })
      handleClose()
    } catch (err: any) {
      setError(err.message || 'Failed to record payment')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setAmount(balanceAmount.toString())
      setPaymentMethod('bank_transfer')
      setPaymentDate(new Date())
      setTransactionId('')
      setNotes('')
      setError('')
      onClose()
    }
  }

  const handleAmountChange = (value: string) => {
    // Allow only numbers and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setAmount(value)
    }
  }

  const setFullPayment = () => {
    setAmount(balanceAmount.toString())
  }

  const remainingBalance = balanceAmount - (parseFloat(amount) || 0)

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          Record Payment
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Invoice: {invoice.invoice_number}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Invoice Summary */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Total Amount
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  ₹{invoice.total_amount.toLocaleString('en-IN')}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Paid Amount
                </Typography>
                <Typography variant="h6" fontWeight={600} color="success.main">
                  ₹{invoice.paid_amount.toLocaleString('en-IN')}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Balance Amount
                </Typography>
                <Typography variant="h6" fontWeight={600} color="error.main">
                  ₹{balanceAmount.toLocaleString('en-IN')}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Remaining After Payment
                </Typography>
                <Typography 
                  variant="h6" 
                  fontWeight={600}
                  color={remainingBalance <= 0 ? 'success.main' : 'warning.main'}
                >
                  ₹{Math.max(0, remainingBalance).toLocaleString('en-IN')}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Payment Form */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Payment Amount"
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        size="small"
                        onClick={setFullPayment}
                        sx={{ textTransform: 'none' }}
                      >
                        Full Payment
                      </Button>
                    </InputAdornment>
                  ),
                }}
                helperText={
                  parseFloat(amount) > 0 && parseFloat(amount) < balanceAmount
                    ? 'This will be a partial payment'
                    : parseFloat(amount) === balanceAmount
                    ? 'This will fully settle the invoice'
                    : ''
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Payment Method"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                {paymentMethods.map((method) => (
                  <MenuItem key={method.value} value={method.value}>
                    {method.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Payment Date"
                  value={paymentDate}
                  onChange={(newValue) => newValue && setPaymentDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Transaction ID / Reference Number"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                placeholder="Enter transaction ID or reference number"
                helperText="Optional: Bank transaction ID, cheque number, or UPI reference"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add any additional notes about this payment"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !amount || parseFloat(amount) <= 0}
        >
          {loading ? 'Recording...' : 'Record Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PaymentRecordDialog
